import {
    Button,
    Collapse,
    createStyles,
    Paper,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import moment from "moment";
import "moment-timezone";
import {
    enqueueSnackbar,
    closeSnackbar,
    SnackbarKey,
    SnackbarProviderProps
} from "notistack";
import React, { DetailedHTMLProps } from "react";
import { connect } from "react-redux";
import {
    Link,
    LinkProps,
    Redirect,
    RouteComponentProps
} from "react-router-dom";
import { bindActionCreators } from "redux";
import { IParcelDetail } from "../../models/parcel/IParcelDetail";
import * as Roles from "../../models/security/Roles";
import Constants from "../../models/utility/Constants";
import { LoadState } from "../../models/utility/LoadState";
import SecurityProvider, {
    userHasRole
} from "../../providers/SecurityProvider";
import { IAppDispatch, IAppState } from "../../store";
import * as ParcelStore from "../../store/ParcelStore";
import * as UserStore from "../../store/UserStore";
import * as AppConfigStore from "../../store/AppConfigStore";
import * as FullScreenCss from "../../styles/FullScreenCss";
import ConfirmBidDialog from "../dialogs/ConfirmBidDialog";
import ParcelClosedDialog from "../dialogs/ParcelClosedDialog";
import FullscreenLoader from "../layout/FullscreenLoader";
import BannerButton from "../shared/BannerButton";
import EditButton from "../shared/EditButton";
import ParcelColumnHeaders from "../shared/parcels/ParcelColumnHeaders";
import ParcelPanel from "../shared/parcels/ParcelPanel";
import SectionBanner from "../shared/SectionBanner";
import AuctionTimerCard from "../shared/timers/AuctionTimerCard";
import NotFound from "./NotFound";
import { Info } from "@material-ui/icons";
import { IConnectionStoreState } from "../../api/connectionSlice";
import bellAudio from "../../assets/bell.mp3";
import NumberFormat from "react-number-format";
import ApprovalRequiredDialog from "../dialogs/ApprovalRequiredDialog";
import AuctionWatcherWrapper from "../shared/AuctionWatcherWrapper";

type ParcelsProps = ParcelStore.IParcelStoreState &
    UserStore.IUserStoreState &
    AppConfigStore.IAppConfigStoreState &
    IConnectionStoreState &
    typeof ParcelStore.actionCreators &
    typeof UserStore.actionCreators &
    SnackbarProviderProps &
    WithStyles<typeof styles | typeof FullScreenCss.styles> &
    RouteComponentProps<{ url: string }>;

const styles = (theme: Theme) =>
    createStyles({
        bodyContent: {
            maxWidth: theme.bodyContent.maxWidth,
            width: "100%"
        },
        mapPosition: {
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
        },
        paper: {
            alignItems: "center",
            display: "flex",
            flexDirection: "column"
        },
        parcelStatusHeader: {
            [theme.breakpoints.down("sm")]: {
                marginBottom: theme.spacing(3),
                marginLeft: theme.spacing(4),
                marginRight: theme.spacing(4),
                marginTop: theme.spacing(3)
            },
            [theme.breakpoints.down("xs")]: {
                marginLeft: theme.spacing(),
                marginRight: theme.spacing()
            },
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            marginBottom: theme.spacing(4),
            marginLeft: theme.spacing(6),
            marginRight: theme.spacing(6),
            marginTop: theme.spacing(4)
        },
        parcelStatusHeaderLine: {
            border: `4px double ${theme.palette.primary.main}`
        },
        parcelStatusHeaderLineDiv: { flex: "1 1 auto" },
        parcelStatusHeaderText: {
            [theme.breakpoints.down("xs")]: { fontSize: 20 },
            fontWeight: "bold",
            paddingLeft: 10,
            paddingRight: 10,
            color: theme.palette.common.white
        },
        section: {
            [theme.breakpoints.down("xs")]: {
                paddingLeft: theme.bodyContent.mobilePadding,
                paddingRight: theme.bodyContent.mobilePadding
            },
            backgroundColor: theme.palette.background.paper,
            paddingLeft: theme.bodyContent.desktopPadding,
            paddingRight: theme.bodyContent.desktopPadding,
            position: "relative",
            width: "100%"
        },
        sectionContent: {
            [theme.breakpoints.down("xs")]: {
                paddingBottom: theme.bodyContent.mobilePadding,
                paddingTop: theme.bodyContent.mobilePadding
            },
            margin: "0 auto",
            maxWidth: theme.bodyContent.maxWidth,
            paddingBottom: theme.bodyContent.desktopPadding,
            paddingTop: theme.bodyContent.desktopPadding,
            width: "100%"
        },
        timeCard: {
            [theme.breakpoints.down("sm")]: { marginBottom: theme.spacing(3) },
            display: "flex",
            justifyContent: "center",
            marginBottom: theme.spacing(5)
        },
        warningIcon: {
            paddingTop: 10,
            float: "left",
            color: "rgba(248, 179, 50, 1)",
            marginRight: theme.spacing(),
            opacity: 0.9
        },
        notificationText: {
            paddingLeft: 10,
            float: "right",
            color: theme.palette.common.white
        },
        notificationLinkText: {
            cursor: "pointer"
        },
        closeNotification: {
            position: "absolute",
            top: "10%",
            right: "5%",
            float: "right",
            color: "#D8D8D8",
            cursor: "pointer",
            paddingLeft: 10
        },
        ...FullScreenCss.styles(theme)
    });

class Parcels extends React.PureComponent<ParcelsProps, {}> {
    private errorSnackbar: SnackbarKey | null;
    private errorRefreshSnackbar: SnackbarKey | null;
    private parcelUpdateSnackbar: SnackbarKey | null;
    private newBidSnackbars: Array<string>;

    constructor(props: ParcelsProps) {
        super(props);

        this.errorSnackbar = null;
        this.errorRefreshSnackbar = null;
        this.parcelUpdateSnackbar = null;
        this.newBidSnackbars = [];
    }

    public componentDidMount() {
        this.props.loadParcelList(this.props.match.params.url);
    }

    public componentDidUpdate(prevProps: ParcelsProps) {
        this.auctionLiveActions(prevProps);
        this.auctionClosedActions(prevProps);

        this.connectionErrorActions();
        this.updateParcelActions();
        this.openNewBidSnackbar(prevProps);
    }

    public componentWillUnmount() {
        this.closeErrorSnackbar();
        this.closeErrorRefreshSnackbar();
        this.closeParcelUpdateSnackbar();
        //this.closeNewBidSnackbar();
    }

    public render() {
        if (this.props.redirectToLogin) {
            return <Redirect to={Constants.Routes.Login()} />;
        }

        if (this.props.parcelsLoadState !== LoadState.Loaded) {
            return <FullscreenLoader />;
        }

        const classes = this.props.classes;
        const auctionInfo = this.props.auctionInfo;

        if (!auctionInfo.auctionUrl) {
            return <NotFound />;
        }

        const live = auctionInfo.auctionLive;
        Constants.SetDocumentTitle(`${auctionInfo.auctionName} - Parcels`);

        const activeParcels = this.props.parcels.filter(
            (e) => !e.isCompleted && e.isLive && e.isActive
        );
        const upcomingParcels = this.props.parcels.filter(
            (e) => !e.isCompleted && !e.isLive && e.isActive
        );
        const closedParcels = this.props.parcels.filter(
            (e) => e.isCompleted && e.isActive
        );

        const isAdmin = userHasRole(Roles.Administrator)(this.props.user);

        return (
            <div>
                <AuctionWatcherWrapper
                    auctionId={auctionInfo.auctionId}
                    userId={this.props.user?.id}
                />
                <ConfirmBidDialog />
                <ParcelClosedDialog />
                <ApprovalRequiredDialog
                    isApprovedAuctionBidder={
                        this.props.auctionInfo.isApprovedAuctionBidder
                    }
                    isAdmin={isAdmin}
                    isUserLoggedIn={!!this.props.user?.id}
                />

                <Paper className={classes.paper} elevation={2}>
                    <SectionBanner
                        leftSideText={auctionInfo.auctionName + " - Parcels"}
                        leftSideComponent={
                            isAdmin ? (
                                <EditButton
                                    component={this.parcelDetailLink(
                                        auctionInfo.auctionUrl
                                    )}
                                />
                            ) : undefined
                        }
                        rightSideComponent={
                            <BannerButton
                                arrowLeft={true}
                                text="Back to Auction Details"
                                link={this.backLink}
                            />
                        }
                    />
                    <div className={classes.section}>
                        <div className={classes.sectionContent}>
                            <Collapse
                                in={!live && closedParcels.length === 0}
                                mountOnEnter={true}
                                unmountOnExit={true}
                            >
                                <div className={classes.timeCard}>
                                    <AuctionTimerCard
                                        auctionId={auctionInfo.auctionId}
                                        auctionDate={
                                            auctionInfo.auctionStartDate
                                        }
                                    />
                                </div>
                            </Collapse>

                            {activeParcels.length > 0 && (
                                <div>
                                    <Collapse
                                        in={true}
                                        mountOnEnter={true}
                                        unmountOnExit={true}
                                    >
                                        {this.renderParcelStatusHeader(
                                            "textPrimary",
                                            "Active Parcels"
                                        )}
                                    </Collapse>
                                    <ParcelColumnHeaders
                                        completed={false}
                                        isLive={true}
                                        showMobile={false}
                                    />
                                    <div>
                                        {activeParcels.map((e) => (
                                            <Collapse
                                                in={!e.isCompleted}
                                                mountOnEnter={true}
                                                unmountOnExit={true}
                                                key={e.parcelId}
                                            >
                                                <ParcelPanel
                                                    isLive={e.isLive}
                                                    canPlaceBid={this.canPlaceBid(
                                                        e
                                                    )}
                                                    completed={e.isCompleted}
                                                    parcel={e}
                                                    auctionUrl={
                                                        auctionInfo.auctionUrl
                                                    }
                                                    hasAcceptedBids={
                                                        e.hasAcceptedBids
                                                    }
                                                    salesPageUrl={
                                                        this.props.salesPageUrl
                                                    }
                                                />
                                            </Collapse>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {upcomingParcels.length > 0 && (
                                <div>
                                    <Collapse
                                        in={true}
                                        mountOnEnter={true}
                                        unmountOnExit={true}
                                    >
                                        {this.renderParcelStatusHeader(
                                            "textPrimary",
                                            "Upcoming Parcels"
                                        )}
                                    </Collapse>
                                    <ParcelColumnHeaders
                                        completed={false}
                                        isLive={false}
                                        showMobile={false}
                                    />
                                    <div>
                                        {upcomingParcels.map((e) => (
                                            <Collapse
                                                in={true}
                                                mountOnEnter={true}
                                                unmountOnExit={true}
                                                key={e.parcelId}
                                            >
                                                <ParcelPanel
                                                    isLive={e.isLive}
                                                    canPlaceBid={this.canPlaceBid(
                                                        e
                                                    )}
                                                    completed={e.isCompleted}
                                                    parcel={e}
                                                    auctionUrl={
                                                        auctionInfo.auctionUrl
                                                    }
                                                    hasAcceptedBids={
                                                        e.hasAcceptedBids
                                                    }
                                                    salesPageUrl={
                                                        this.props.salesPageUrl
                                                    }
                                                />
                                            </Collapse>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {closedParcels.length > 0 && (
                                <div>
                                    <Collapse
                                        in={true}
                                        mountOnEnter={true}
                                        unmountOnExit={true}
                                    >
                                        {this.renderParcelStatusHeader(
                                            "textSecondary",
                                            "Closed Parcels"
                                        )}
                                    </Collapse>
                                    <ParcelColumnHeaders
                                        completed={true}
                                        isLive={false}
                                        showMobile={false}
                                    />
                                    <div>
                                        {closedParcels.map((e) => (
                                            <Collapse
                                                in={true}
                                                mountOnEnter={true}
                                                unmountOnExit={true}
                                                key={e.parcelId}
                                            >
                                                <ParcelPanel
                                                    isLive={e.isLive}
                                                    canPlaceBid={this.canPlaceBid(
                                                        e
                                                    )}
                                                    completed={e.isCompleted}
                                                    parcel={e}
                                                    auctionUrl={
                                                        auctionInfo.auctionUrl
                                                    }
                                                    isParcelClosed={true}
                                                    isPrivateSale={
                                                        e.isPrivateSale
                                                    }
                                                    hasAcceptedBids={
                                                        e.hasAcceptedBids
                                                    }
                                                    salesPageUrl={
                                                        this.props.salesPageUrl
                                                    }
                                                />
                                            </Collapse>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Paper>
            </div>
        );
    }

    private renderParcelStatusHeader = (
        color: "textPrimary" | "textSecondary",
        text: string
    ) => {
        const classes = this.props.classes;

        return (
            <div className={classes.parcelStatusHeader}>
                <div className={classes.parcelStatusHeaderLineDiv}>
                    <hr className={classes.parcelStatusHeaderLine} />
                </div>
                <div>
                    <Typography
                        className={classes.parcelStatusHeaderText}
                        component="span"
                        variant="h5"
                        color={color}
                    >
                        {text}
                    </Typography>
                </div>
                <div className={classes.parcelStatusHeaderLineDiv}>
                    <hr className={classes.parcelStatusHeaderLine} />
                </div>
            </div>
        );
    };

    private renderNotification = (Parcel: IParcelDetail, Key: string) => {
        const classes = this.props.classes;

        const audio = new Audio(bellAudio);
        audio.play().catch((e) => {
            // This will trigger if the user hasn't intereacted with the page yet
            //   or denied permission to play audio. Silently ignore this error,
            //   because the audio is not critical to the user experience.
            console.error("Audio play error", e);
        });

        return (
            <div>
                <div className={classes.warningIcon}>
                    <Info />
                </div>
                <div className={classes.closeNotification}>
                    <span onClick={() => this.closeNewBidSnackbar(Key)}>
                        <b>x</b>
                    </span>
                </div>
                <div className={classes.notificationText}>
                    <b>{Parcel.name}</b> Current Bid{" "}
                    <b>
                        <NumberFormat
                            value={Parcel.lastAcceptedBid}
                            thousandSeparator={true}
                            prefix="$"
                            decimalScale={0}
                            fixedDecimalScale={true}
                            displayType="text"
                        />
                    </b>
                    !
                    <br />
                    <span
                        className={classes.notificationLinkText}
                        onClick={() => this.scrollToParcel(Parcel.parcelId)}
                    >
                        <u>Click Here</u> to View
                    </span>
                </div>
            </div>
        );
    };
    private scrollToParcel(id: string) {
        const parcel = document.getElementById(id);
        if (parcel) {
            parcel.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center"
            });
        }
    }

    private backLink = (
        props: DetailedHTMLProps<
            React.AnchorHTMLAttributes<HTMLAnchorElement>,
            HTMLAnchorElement
        >
    ) => {
        const auctionUrl = Constants.Routes.AuctionDetail(
            this.props.match.params.url,
            { baseUrl: this.props.salesPageUrl }
        ).href;
        return (
            <a href={auctionUrl} {...props}>
                {props.children}
            </a>
        );
    };

    private parcelDetailLink =
        (auctionUrl: string) => (props: Partial<LinkProps>) =>
            (
                <Link
                    to={{
                        pathname:
                            Constants.Routes.AdminAuctionDetail(auctionUrl),
                        state: { selectedTab: 1 }
                    }}
                    {...props}
                />
            );

    private canPlaceBid = (e: IParcelDetail) =>
        !!(
            this.props.user &&
            SecurityProvider.CanPlaceBidOnParcel(
                this.props.user,
                e,
                this.props.auctionInfo
            )
        );

    /* COMPONENT DID UPDATE METHODS */
    private auctionLiveActions = (prevProps: ParcelsProps) => {
        const now = moment(Constants.CurrentDate()).local();
        const start = moment(this.props.auctionInfo.auctionStartDate).local();

        // show auction live snackbar for a few seconds after going live.
        if (
            this.props.auctionInfo.auctionLive &&
            this.props.auctionInfo.auctionLive !==
                prevProps.auctionInfo.auctionLive &&
            start <= now &&
            start.add(3, "seconds") >= now
        ) {
            this.openAuctionLiveSnackbar();
        }
    };

    private getLastClosedAuction = () => {
        return this.props.parcels.reduce(
            (a: IParcelDetail, b: IParcelDetail) => {
                if (a.auctionEndDate > b.auctionEndDate) {
                    return a;
                }
                return b;
            }
        );
    };

    private auctionClosedActions = (prevProps: ParcelsProps) => {
        const now = moment(Constants.CurrentDate()).local();
        const end =
            this.props.parcels.length > 0
                ? moment(this.getLastClosedAuction().auctionEndDate).local()
                : null;

        // show auction closed popup for a few seconds after close.
        if (
            !this.props.auctionInfo.auctionLive &&
            this.props.auctionInfo.auctionLive !==
                prevProps.auctionInfo.auctionLive &&
            !!end &&
            end <= now &&
            end.add(5, "seconds") >= now
        ) {
            this.openAuctionClosedSnackbar();
        }
    };

    // show error snackbars if connection error occurs
    private connectionErrorActions = () => {
        if (this.props.showReconnectingSnackbarMessage) {
            this.openErrorSnackbar();
        } else {
            this.closeErrorSnackbar();
        }

        if (this.props.showRefreshSnackbarMessage) {
            this.openErrorRefreshSnackbar();
        } else {
            this.closeErrorRefreshSnackbar();
        }

        if (this.props.showConnectedSnackbarMessage) {
            this.openOnlineSnackbar();
        }
    };

    private updateParcelActions = () => {
        if (this.props.showParcelUpdateMessage) {
            this.openParcelUpdateSnackbar();
        }
    };

    /* SNACKBAR METHODS */
    private openOnlineSnackbar = () =>
        enqueueSnackbar("Back online!", {
            anchorOrigin: { horizontal: "left", vertical: "bottom" },
            variant: "success"
        });

    private openAuctionLiveSnackbar = () =>
        enqueueSnackbar("Auction is now live! Start bidding!", {
            anchorOrigin: { horizontal: "left", vertical: "bottom" },
            variant: "success"
        });

    private openAuctionClosedSnackbar = () =>
        enqueueSnackbar("Auction is now closed!", {
            anchorOrigin: { horizontal: "left", vertical: "bottom" },
            variant: "success"
        });

    private openErrorSnackbar = () => {
        if (!this.errorSnackbar) {
            this.errorSnackbar = enqueueSnackbar(
                "Connection interrupted. Reconnecting…",
                {
                    anchorOrigin: { horizontal: "left", vertical: "bottom" },
                    persist: true,
                    variant: "warning"
                }
            );
        }
    };

    private openErrorRefreshSnackbar = () => {
        if (!this.errorRefreshSnackbar) {
            this.errorRefreshSnackbar = enqueueSnackbar(
                <span className="error-refresh-message">
                    Connection failed. Confirm you are online and retry.
                </span>,
                {
                    action: (
                        <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            onClick={() => window.location.reload()}
                        >
                            Refresh
                        </Button>
                    ),
                    anchorOrigin: { horizontal: "left", vertical: "bottom" },
                    persist: true,
                    variant: "error"
                }
            );
        }
    };

    private openParcelUpdateSnackbar = () => {
        if (!this.parcelUpdateSnackbar) {
            this.parcelUpdateSnackbar = enqueueSnackbar(
                this.props.parcelUpdateMessage,
                {
                    action: (
                        <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            onClick={this.closeParcelUpdateSnackbar}
                        >
                            Close
                        </Button>
                    ),
                    anchorOrigin: { horizontal: "left", vertical: "bottom" },
                    persist: true,
                    preventDuplicate: true,
                    variant: "info"
                }
            );
        }
    };
    private openNewBidSnackbar = (prevProps: ParcelsProps) => {
        if (this.props.parcels.length > 0 && prevProps.parcels.length > 0) {
            this.props.parcels.forEach((e1) => {
                const e2 = prevProps.parcels.filter(
                    (p) => p.parcelId === e1.parcelId
                )[0];
                const key =
                    e1.parcelId +
                    "-" +
                    (e1.lastAcceptedBid ?? "") +
                    "-" +
                    e1.hasAcceptedBids;

                if (
                    e2.lastAcceptedBid != null &&
                    e1.lastAcceptedBid != null &&
                    !e1.isCompleted &&
                    e1.isLive &&
                    e1.isActive
                ) {
                    if (
                        e2.lastAcceptedBid < e1.lastAcceptedBid ||
                        (e2.lastAcceptedBid === e1.lastAcceptedBid &&
                            !e2.hasAcceptedBids &&
                            e1.hasAcceptedBids)
                    ) {
                        if (
                            this.newBidSnackbars.find((e) => e === key) == null
                        ) {
                            this.newBidSnackbars.push(key);
                            enqueueSnackbar(this.renderNotification(e1, key), {
                                anchorOrigin: {
                                    horizontal: "left",
                                    vertical: "bottom"
                                },
                                key: key,
                                onClose: () => {
                                    this.newBidSnackbars =
                                        this.newBidSnackbars.filter(
                                            (x) => x !== key
                                        );
                                }
                            });
                        }
                    }
                }
            });
        }
    };

    private closeErrorSnackbar = () => {
        if (this.errorSnackbar) {
            closeSnackbar(this.errorSnackbar);
            this.errorSnackbar = null;
        }
    };

    private closeErrorRefreshSnackbar = () => {
        if (this.errorRefreshSnackbar) {
            closeSnackbar(this.errorRefreshSnackbar);
            this.errorRefreshSnackbar = null;
        }
    };

    private closeParcelUpdateSnackbar = () => {
        if (this.parcelUpdateSnackbar) {
            closeSnackbar(this.parcelUpdateSnackbar);
            this.parcelUpdateSnackbar = null;
        }
    };
    private closeNewBidSnackbar = (Key: string) => {
        try {
            this.newBidSnackbars = this.newBidSnackbars.filter(
                (x) => x !== Key
            );
            closeSnackbar(Key);
        } catch (err) {
            console.error(err);
            //Do nothing
        }
    };
}

const reduxState = (state: IAppState) =>
    Object.assign(
        {},
        state.parcelStoreState,
        state.userStoreState,
        state.appConfigStoreState,
        state.connectionStoreState
    );
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(
        Object.assign({}, ParcelStore.actionCreators, UserStore.actionCreators),
        dispatch
    );

export default withStyles(styles)(connect(reduxState, reduxDispatch)(Parcels));
